/** The units the pantry can actually do arithmetic with.
 *
 * This mirrors `backend/recipes/units.py`, which is the source of truth: the
 * pantry is only useful if what you record can be subtracted from what a
 * recipe asks for, and that comparison happens server-side against exactly
 * these sets. A unit outside them is not wrong so much as inert -- it stores
 * fine and then never cancels anything out.
 *
 * `backend/recipes/test_units_match_frontend.py` fails if the two drift.
 */

/** Spellings the backend folds together, so a stored "tablespoon" still
 *  selects the "tbsp" option rather than looking like an unknown unit.
 *
 *  Only the abbreviations and the one irregular plural are written out. The
 *  regular plurals are built from the counting words below, which is how
 *  `sachets` and `loaves` came to be missing from the hand-written list. */
const IRREGULAR_PLURALS: Record<string, string> = { loaf: 'loaves' };

const ABBREVIATIONS: Record<string, string> = {
  tablespoon: 'tbsp', tablespoons: 'tbsp', tbsps: 'tbsp', tbs: 'tbsp',
  teaspoon: 'tsp', teaspoons: 'tsp', tsps: 'tsp',
  cups: 'cup', ounce: 'oz', ounces: 'oz', ozs: 'oz',
  pound: 'lb', pounds: 'lb', lbs: 'lb',
  gram: 'g', grams: 'g',
  pkg: 'package', pkgs: 'package',
  count: 'ct', each: 'ct', ea: 'ct',
};

/* Counting words, grouped by what the word actually names. The grouping
   decides what a unit may be cancelled or rounded against — see
   `backend/recipes/units.py`, which holds the reasoning and the same four
   sets. Here it also gives the dropdown three short lists instead of one
   twenty-six-line one. */

/** One whole thing, as a shop hands it over. */
export const WHOLE_ITEM_UNITS = ['', 'ct', 'piece', 'ear', 'head', 'bulb', 'bunch'];
/** A part of one of those — never sold on its own. */
export const PORTION_UNITS = ['clove', 'sprig', 'slice', 'stalk', 'fillet', 'sheet'];
/** What the food came in. Says nothing about how much is inside. */
export const CONTAINER_UNITS = [
  'can', 'jar', 'bottle', 'box', 'bag', 'tin', 'pouch', 'packet',
  'package', 'container', 'sachet', 'loaf',
];
/** Counted by nobody and measured by nobody. */
export const VAGUE_UNITS = ['pinch'];

export const COUNT_UNITS = [
  ...WHOLE_ITEM_UNITS,
  ...PORTION_UNITS,
  ...CONTAINER_UNITS,
  ...VAGUE_UNITS,
];
export const WEIGHT_UNITS = ['oz', 'lb', 'g', 'kg'];
export const VOLUME_UNITS = ['tsp', 'tbsp', 'cup', 'floz', 'pint', 'quart', 'ml', 'l'];

const plural = (unit: string) =>
  IRREGULAR_PLURALS[unit] ?? unit + (/(s|x|ch|sh)$/.test(unit) ? 'es' : 's');

const ALIASES: Record<string, string> = { ...ABBREVIATIONS };
for (const unit of COUNT_UNITS) {
  if (unit !== '' && unit !== 'ct') ALIASES[plural(unit)] = unit;
}

/** Grouped for the dropdown. The counting words come first because most of
 *  what turns up in a cupboard is a jar or a bag rather than a measured
 *  amount — and they come as three short lists, because scrolling past
 *  `sachet` to reach `oz` is what one long list felt like. */
export const UNIT_GROUPS: { label: string; units: string[] }[] = [
  { label: 'Whole things', units: WHOLE_ITEM_UNITS },
  { label: 'Containers', units: CONTAINER_UNITS },
  { label: 'Parts of one', units: PORTION_UNITS },
  { label: 'Weight', units: WEIGHT_UNITS },
  { label: 'Volume', units: VOLUME_UNITS },
  { label: 'Other', units: VAGUE_UNITS },
];

const LABELS: Record<string, string> = {
  '': 'whole things',
  ct: 'count',
  floz: 'fl oz',
  l: 'litres',
  ml: 'ml',
  g: 'grams',
  kg: 'kg',
};

export function unitLabel(unit: string): string {
  return LABELS[unit] ?? unit;
}

/** The same folding `normalize_unit` does server-side. */
export function normalizeUnit(unit: string): string {
  const cleaned = (unit || '').trim().toLowerCase().replace(/[^a-z]/g, '');
  return ALIASES[cleaned] ?? cleaned;
}

const KNOWN = new Set([...COUNT_UNITS, ...WEIGHT_UNITS, ...VOLUME_UNITS]);

export function isKnownUnit(unit: string): boolean {
  return KNOWN.has(normalizeUnit(unit));
}
